import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { StickyNote, Highlighter, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

interface Annotation {
  id: string;
  type: "note" | "highlight" | "comment";
  content: string | null;
  content_ref: Record<string, unknown> | null;
}

interface LearnerAnnotationsProps {
  skillNodeId: string;
  annotationsContainerRef: React.RefObject<HTMLElement | null>;
  notesPanelContainerId?: string;
  notesPanelOpen?: boolean;
  onNotesPanelOpenChange?: (open: boolean) => void;
}

const HIGHLIGHT_COLORS = [
  { name: "Yellow", value: "rgba(250, 204, 21, 0.4)" },
  { name: "Green", value: "rgba(34, 197, 94, 0.35)" },
  { name: "Blue", value: "rgba(59, 130, 246, 0.35)" },
];

function findTextRange(
  root: Node,
  startOffset: number,
  endOffset: number
): { start: { node: Text; offset: number }; end: { node: Text; offset: number } } | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = 0;
  let startNode: Text | null = null;
  let startOff = 0;
  let endNode: Text | null = null;
  let endOff = 0;

  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const len = node.textContent?.length ?? 0;
    if (!startNode && current + len > startOffset) {
      startNode = node;
      startOff = startOffset - current;
    }
    if (!endNode && current + len >= endOffset) {
      endNode = node;
      endOff = endOffset - current;
      break;
    }
    current += len;
  }
  if (startNode && endNode) {
    return { start: { node: startNode, offset: startOff }, end: { node: endNode, offset: endOff } };
  }
  return null;
}

export default function LearnerAnnotations({
  skillNodeId,
  annotationsContainerRef,
  notesPanelContainerId,
  notesPanelOpen = false,
  onNotesPanelOpenChange,
}: LearnerAnnotationsProps) {
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [highlights, setHighlights] = useState<Annotation[]>([]);
  const [comments, setComments] = useState<Annotation[]>([]);
  const [showNotes, setShowNotes] = useState(true);
  const useNotesPanel = Boolean(notesPanelContainerId);
  const [selectionToolbar, setSelectionToolbar] = useState<{
    x: number;
    y: number;
    selection: Selection;
    selectedText: string;
    container: HTMLElement;
  } | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [showCommentForm, setShowCommentForm] = useState(false);
  const selectionRef = useRef<Selection | null>(null);
  const selectedTextRef = useRef("");
  const { toast } = useToast();

  const fetchAnnotations = useCallback(async () => {
    const { data, error } = await supabase
      .from("learner_annotations")
      .select("*")
      .eq("skill_node_id", skillNodeId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Failed to fetch annotations:", error);
      return;
    }
    const list = (data || []) as Annotation[];
    const noteAnn = list.find((a) => a.type === "note");
    setNote(noteAnn?.content ?? "");
    setHighlights(list.filter((a) => a.type === "highlight"));
    setComments(list.filter((a) => a.type === "comment"));
  }, [skillNodeId]);

  useEffect(() => {
    fetchAnnotations();
  }, [fetchAnnotations]);

  const saveNote = useCallback(async () => {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return;
    setNoteSaving(true);
    const existing = await supabase
      .from("learner_annotations")
      .select("id")
      .eq("user_id", user.user.id)
      .eq("skill_node_id", skillNodeId)
      .eq("type", "note")
      .maybeSingle();

    const payload = {
      user_id: user.user.id,
      skill_node_id: skillNodeId,
      type: "note",
      content: note,
      content_ref: null,
      updated_at: new Date().toISOString(),
    };

    if (existing.data) {
      await supabase.from("learner_annotations").update(payload).eq("id", existing.data.id);
    } else {
      await supabase.from("learner_annotations").insert(payload);
    }
    setNoteSaving(false);
    toast({ title: "Note saved" });
  }, [skillNodeId, note, toast]);

  const applyHighlight = useCallback(
    async (selection: Selection, selectedText: string, color: string) => {
      const container = annotationsContainerRef.current;
      if (!container) return;

      const range = selection.getRangeAt(0);
      const preRange = document.createRange();
      preRange.selectNodeContents(container);
      preRange.setEnd(range.startContainer, range.startOffset);
      const startOffset = preRange.toString().length;
      const endOffset = startOffset + selectedText.length;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const { data, error } = await supabase
        .from("learner_annotations")
        .insert({
          user_id: userData.user.id,
          skill_node_id: skillNodeId,
          type: "highlight",
          content: color,
          content_ref: { startOffset, endOffset, selectedText },
        })
        .select()
        .single();

      if (error) {
        toast({ variant: "destructive", title: "Could not save highlight" });
        return;
      }

      const mark = document.createElement("mark");
      mark.style.backgroundColor = color;
      mark.setAttribute("data-annotation-id", data.id);
      try {
        range.surroundContents(mark);
      } catch {
        toast({ variant: "destructive", title: "Could not highlight that selection" });
        return;
      }
      setHighlights((h) => [...h, data]);
      setSelectionToolbar(null);
      toast({ title: "Highlight added" });
    },
    [skillNodeId, annotationsContainerRef, toast]
  );

  const applyComment = useCallback(
    async (selection: Selection, selectedText: string, commentText: string) => {
      const container = annotationsContainerRef.current;
      if (!container || !commentText.trim()) return;

      const range = selection.getRangeAt(0);
      const preRange = document.createRange();
      preRange.selectNodeContents(container);
      preRange.setEnd(range.startContainer, range.startOffset);
      const startOffset = preRange.toString().length;
      const endOffset = startOffset + selectedText.length;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const { data, error } = await supabase
        .from("learner_annotations")
        .insert({
          user_id: userData.user.id,
          skill_node_id: skillNodeId,
          type: "comment",
          content: commentText.trim(),
          content_ref: { startOffset, endOffset, selectedText },
        })
        .select()
        .single();

      if (error) {
        toast({ variant: "destructive", title: "Could not add comment" });
        return;
      }

      const mark = document.createElement("mark");
      mark.style.backgroundColor = "rgba(251, 191, 36, 0.4)";
      mark.setAttribute("data-annotation-id", data.id);
      mark.setAttribute("title", commentText.trim());
      mark.classList.add("cursor-help");
      try {
        range.surroundContents(mark);
      } catch {
        toast({ variant: "destructive", title: "Could not add comment to that selection" });
        return;
      }
      setComments((c) => [...c, data]);
      setSelectionToolbar(null);
      setCommentDraft("");
      setShowCommentForm(false);
      selectionRef.current = null;
      toast({ title: "Comment added" });
    },
    [skillNodeId, annotationsContainerRef, toast]
  );

  useEffect(() => {
    const container = annotationsContainerRef.current;
    if (!container) return;

    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !container.contains(sel.anchorNode)) {
        setSelectionToolbar(null);
        setShowCommentForm(false);
        selectionRef.current = null;
        return;
      }
      const selectedText = sel.toString().trim();
      if (selectedText.length < 2) {
        setSelectionToolbar(null);
        return;
      }
      selectionRef.current = sel;
      selectedTextRef.current = selectedText;
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setSelectionToolbar({
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        selection: sel,
        selectedText,
        container,
      });
    };

    container.addEventListener("mouseup", handleMouseUp);
    return () => container.removeEventListener("mouseup", handleMouseUp);
  }, [annotationsContainerRef]);

  // Restore highlights/comments from DB after content renders
  useEffect(() => {
    const container = annotationsContainerRef.current;
    const toRestore = [...highlights, ...comments].filter((a) => a.content_ref);
    if (!container || toRestore.length === 0) return;

    const fullText = container.textContent ?? "";
    const sorted = [...toRestore].sort(
      (a, b) =>
        ((b.content_ref as { endOffset?: number })?.endOffset ?? 0) -
        ((a.content_ref as { endOffset?: number })?.endOffset ?? 0)
    );

    for (const ann of sorted) {
      const ref = ann.content_ref as { startOffset?: number; endOffset?: number; selectedText?: string };
      if (ref?.startOffset == null || ref?.endOffset == null) continue;
      const { startOffset: start, endOffset: end, selectedText } = ref;
      const slice = fullText.slice(start, end);
      if (slice !== selectedText) continue;
      const rangeInfo = findTextRange(container, start, end);
      if (!rangeInfo) continue;
      const range = document.createRange();
      range.setStart(rangeInfo.start.node, rangeInfo.start.offset);
      range.setEnd(rangeInfo.end.node, rangeInfo.end.offset);
      const mark = document.createElement("mark");
      mark.style.backgroundColor =
        ann.type === "comment" ? "rgba(251, 191, 36, 0.4)" : (ann.content as string) || HIGHLIGHT_COLORS[0].value;
      mark.setAttribute("data-annotation-id", ann.id);
      if (ann.type === "comment" && ann.content) mark.setAttribute("title", ann.content);
      mark.classList.add("cursor-help");
      try {
        range.surroundContents(mark);
      } catch {
        // skip if already wrapped or invalid
      }
    }
  }, [highlights, comments, annotationsContainerRef]);

  const sel = selectionToolbar?.selection ?? selectionRef.current;
  const selText = selectionToolbar?.selectedText ?? selectedTextRef.current;

  const notesForm = (
    <div className="p-4 flex flex-col h-full overflow-auto">
      <Textarea
        placeholder="Add your notes here..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => note.trim() && saveNote()}
        className="min-h-[200px] flex-1 resize-none"
      />
      <Button
        size="sm"
        className="mt-3 shrink-0"
        onClick={() => saveNote()}
        disabled={noteSaving || !note.trim()}
      >
        {noteSaving ? "Saving..." : "Save Note"}
      </Button>
    </div>
  );

  const notesPanelEl =
    useNotesPanel && notesPanelOpen && typeof document !== "undefined"
      ? document.getElementById(notesPanelContainerId!)
      : null;

  return (
    <div className="space-y-4">
      {notesPanelEl && createPortal(notesForm, notesPanelEl)}
      {!useNotesPanel && (
        <Card>
          <CardHeader
            className="cursor-pointer py-4"
            onClick={() => setShowNotes(!showNotes)}
          >
            <CardTitle className="flex items-center justify-between text-base font-medium">
              <span className="flex items-center gap-2">
                <StickyNote className="h-4 w-4" />
                My Notes
              </span>
              {showNotes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardTitle>
          </CardHeader>
          {showNotes && (
            <CardContent className="pt-0">
              <Textarea
                placeholder="Add your notes here..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => note.trim() && saveNote()}
                className="min-h-[120px] resize-none"
              />
              <Button
                size="sm"
                className="mt-2"
                onClick={() => saveNote()}
                disabled={noteSaving || !note.trim()}
              >
                {noteSaving ? "Saving..." : "Save Note"}
              </Button>
            </CardContent>
          )}
        </Card>
      )}

      {selectionToolbar && (
        <div
          className="fixed z-50 flex -translate-x-1/2 -translate-y-full gap-1 rounded-lg border bg-background p-1 shadow-lg"
          style={{ left: selectionToolbar.x, top: selectionToolbar.y }}
        >
          {showCommentForm ? (
            <div className="flex flex-col gap-1 p-1">
              <Textarea
                placeholder="Add a comment..."
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                className="min-h-[60px] w-48 resize-none text-sm"
                autoFocus
              />
              <div className="flex gap-1">
                <Button
                  size="sm"
                  onClick={() => {
                    if (sel && commentDraft.trim()) applyComment(sel, selText, commentDraft);
                  }}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setCommentDraft("");
                    setShowCommentForm(false);
                    setSelectionToolbar(null);
                    selectionRef.current = null;
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {HIGHLIGHT_COLORS.map((c) => (
                <Button
                  key={c.name}
                  size="sm"
                  variant="ghost"
                  title={`Highlight ${c.name}`}
                  onClick={() =>
                    applyHighlight(selectionToolbar.selection, selectionToolbar.selectedText, c.value)
                  }
                >
                  <Highlighter
                    className="h-4 w-4"
                    style={{ color: c.value.replace(/0\.[34]\d*/, "1") }}
                  />
                </Button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                title="Add comment"
                onClick={() => setShowCommentForm(true)}
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Select text to highlight or add a comment.
      </p>
    </div>
  );
}
